import React, { useMemo, useState } from 'react'
import type { CalendarButtonProps } from '../block'
import style from './style.module.scss'
import clsx from 'clsx'
import { RectangularButton } from '../../RectangularButton'
import { toCalendarUrl, toStructureLogoSrc } from '../../../shared/competitionUrls'
import { StructureLogoOrGeneric } from '../../../components/StructureLogoOrGeneric'

type CalendarType = 'apple' | 'google' | 'ical'

export const CalendarPopup = ({ equipes, ext_competitionId }: CalendarButtonProps) => {
    const [selectedEquipe, setSelectedEquipe] = useState<string | undefined>(undefined)
    const [calendarType, setCalendarType] = useState<CalendarType | undefined>(undefined)

    const link = useMemo(() => {
        const equipe = equipes.find((e) => e.id === selectedEquipe)
        if (equipe && calendarType) {
            const calendarPath = toCalendarUrl(ext_competitionId, equipe?.ext_structureId ?? 0)
            const webcallUrl = `webcal://${calendarPath}`
            switch (calendarType) {
                case 'apple':
                    return webcallUrl
                case 'ical':
                    return `https://${calendarPath}` // For download we use https
                case 'google':
                    const google = encodeURIComponent(`http://${calendarPath}`) // This google method require http link (not https)
                    return `https://calendar.google.com/calendar/render?cid=${google}`
            }
        }
        return undefined
    }, [selectedEquipe, calendarType, equipes, ext_competitionId])

    return (
        <div className={style.component}>
            <h2>Choisissez un calendrier</h2>
            <div className={style.helperText}>
                Vous pouvez ajouter le calendrier de votre club préféré à votre agenda pour ne
                manquer aucune rencontre. Choisissez simplement votre club et téléchargez le
                calendrier des matchs à venir.
            </div>
            <div className={style.step}>
                <span>1.</span> Sélectionnez un type de calendrier
            </div>

            <div className={style.selectType}>
                <div className={style.buttons}>
                    <div
                        className={clsx(
                            style.buttonType,
                            calendarType === 'apple' && style.selected,
                        )}
                        onClick={() => setCalendarType('apple')}
                    >
                        <i className="fi fi-brands-apple" />
                        <span>iCal Apple</span>
                    </div>

                    <div
                        onClick={() => setCalendarType('google')}
                        className={clsx(
                            style.buttonType,
                            calendarType === 'google' && style.selected,
                        )}
                    >
                        <i className="fi fi-brands-google" />
                        <span>Google calendar</span>
                    </div>

                    <div
                        onClick={() => setCalendarType('ical')}
                        className={clsx(
                            style.buttonType,
                            calendarType === 'ical' && style.selected,
                        )}
                    >
                        <i className="fi fi-rs-calendar" />
                        <span>iCal (.ics)</span>
                    </div>
                </div>

                <RectangularButton
                    flatIcon="plus-small"
                    type="a"
                    href={link}
                    rel="noreferrer"
                    className={!link ? style.linkDisabled : undefined}
                >
                    Ajouter le calendrier
                </RectangularButton>
            </div>

            <div className={style.step}>
                <span>2.</span> Sélectionnez une équipe
            </div>
            <div className={style.equipeList}>
                {equipes.map((equipe) => (
                    <div
                        className={clsx(
                            style.equipe,
                            selectedEquipe === equipe.id && style.selected,
                        )}
                        key={equipe.id}
                        onClick={() => setSelectedEquipe(equipe.id)}
                    >
                        {equipe.logoActif && equipe.logo && (
                            <StructureLogoOrGeneric
                                className={style.logo}
                                imageUrl={toStructureLogoSrc(equipe.logo, 64)}
                                title={`${equipe.libelle}`}
                            />
                        )}
                        <span className={style.libelle}>{equipe.libelle}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}
